/**
 * **The event loop** (`decisions/0212` items 1, 3, 6, 7): every change inside an intent is an event
 * that is proposed, offered to replacements, happens, and is then offered to triggers. This module
 * is the library; the referee creates one loop per intent and threads it down (on the resolution
 * ledger, `effects/resolutionLedger.ts`), and `fireTrigger` raises its family's event into it.
 *
 * **Staged in** (`event-engine-kernel` Phase 2). At this slice:
 *   - "happens" records the event in {@link EventLoop.log}. Its grounded steps fold into the
 *     loop's scratch state as they land ({@link EventLoop.land}), and listeners read that scratch
 *     ({@link EventLoop.scratch}), so a later listener sees what an earlier one did (Phase 5);
 *   - no replacement listener is wired yet ({@link Stage.replacements} is the seam). A
 *     replacement may bring follow-ups ({@link Replaced.then}) that land right after the event it
 *     rewrote: Barrel Roll's flight after the miss it caused;
 *   - the kinds raised are the trigger families, costs ({@link CostEvent}), declarations
 *     ({@link ActionDeclaredEvent}) and the to-hit lane's rolls, attempts, outcomes and checks
 *     (`rollEvents.ts`), and the damage packet (`damageEvents.ts`).
 *
 * **Order between events** (item 3). A trigger that raises an event raises it inside
 * {@link EventLoop.raise}, so consequences resolve depth-first. An event that opens a new action (a
 * forced attack) is queued with {@link EventLoop.queueAction} and runs first-in, first-out after the
 * current action finishes ({@link EventLoop.nextAction}).
 *
 * **Dice** (item 6). The loop owns the intent's one recording dice source, so a halt (a thrown
 * `NeedsDecisionSignal`, re-run from the top) echoes every die drawn before it.
 */
import { applyResolved, type Cell, type EncounterState, type ResolvedStep } from "@lancer-vtt/domain";
import type { DiceSource } from "../../dice";
import type { GameEvent } from "./gameEvents";
import type { ForcedAttackRequest } from "../effects/atoms";
import type { TriggerFamily } from "../effects/family";
import type { TriggerEvent } from "../effects/predicate";
import type { AttackAttemptEvent, AttackForcedMissEvent, AttackOutcomeEvent, AttackResistedEvent, CheckEvent, RollEvent } from "./rollEvents";
import type { DamagePacketEvent } from "./damageEvents";
import type { TrackEvent } from "./trackEvents";

/**
 * A trigger family's event: the payload its fire site builds (`FireContext.event`), plus who it is
 * about. `subject` is the combatant the event centres on (the attack's target, the carrier at a turn
 * edge); `actor` is who caused it (the attacker).
 */
export type FamilyEvent = { kind: "family"; family: TriggerFamily; payload?: TriggerEvent; subject?: string; actor?: string };

/**
 * A cost (`decisions/0211`): raised when a stage owes it and settled once, at its action's end, by
 * whoever owns the action's resolution ledger (`settleEvents`).
 */
export type CostEvent = { kind: "cost"; cost: GameEvent };

/**
 * **A declaration** (`decisions/0212` item 8): an actor declares an action. It is the root cause of
 * everything the action does; a forced attack's declaration is caused by the event that forced it.
 * `action` names what was declared: the economy action id, else the intent's own id (its weapon or
 * activation, else its type), or `"forced_attack"` for a forced one. `targets` are the units it names.
 */
export type ActionDeclaredEvent = { kind: "action_declared"; actor: string; action: string; isReaction: boolean; targets: string[] };

/**
 * **A turn edge** (`event-engine-lifecycle` D1): `combatant`'s turn starts or ends. `turnOrdinal` is
 * that turn's `ActivationState.turnOrdinal`, which the duration expiries compare against.
 */
export type TurnEdgeEvent = { kind: "turn_started" | "turn_ended"; combatant: string; turnOrdinal: number };

/** **A round edge**: `round` ends, or starts. `end_round` raises the two back to back. */
export type RoundEdgeEvent = { kind: "round_ended" | "round_started"; round: number };

/**
 * **An attack resolved** (`event-engine-lifecycle` D14): `attacker`'s attack or forced save has
 * finished against every target. Raised once per action, after the per-target events.
 */
export type AttackResolvedEvent = { kind: "attack_resolved"; attacker: string };

/**
 * **A move is declared** (`event-engine-lifecycle` D1): `mover` declares a move from `from` to
 * `to`, before any of it happens. A move is not an action, so this is not `action_declared`. The
 * movement interrupts (Overwatch, an authored move reaction, Impending Threat) fire on it, before
 * the move resolves (`decisions/0212` item 3).
 */
export type MoveDeclaredEvent = { kind: "move_declared"; mover: string; from: Cell; to: Cell; path?: readonly Cell[]; voluntary: boolean };

/**
 * **A move landed** (`event-engine-lifecycle` D1): `mover` went from `from` to `to` along `path`.
 * `by` names the entry point that moved it (`referee/movement.ts`).
 */
export type MovedEvent = { kind: "moved"; mover: string; from?: Cell; to?: Cell; path?: readonly Cell[]; voluntary: boolean; by: string };

/** **A movement ended**: `mover`'s whole movement is done (a Covering Fire split's rest ends it, not its first half). */
export type MovementEndedEvent = { kind: "movement_ended"; mover: string };

/** **A source is gone** (`event-engine-lifecycle` D1): `combatant` was destroyed or removed, so the expiries keyed to its turns end. */
export type SourceGoneEvent = { kind: "source_gone"; combatant: string };

/** Every kind the loop carries. Grows per `decisions/0212` item 1. */
export type KernelEvent = FamilyEvent | CostEvent | ActionDeclaredEvent | RollEvent | AttackAttemptEvent | AttackOutcomeEvent | CheckEvent | AttackForcedMissEvent | AttackResistedEvent | TurnEdgeEvent | RoundEdgeEvent | AttackResolvedEvent | SourceGoneEvent | MoveDeclaredEvent | MovedEvent | MovementEndedEvent | DamagePacketEvent | TrackEvent;

/** What raised an event: the event it reacts to and the listener that raised it. `null` is a root. */
export type Cause = { event: string; listener?: string } | null;

/** An event that happened: its id, what it was, and its cause. */
export type Happened = { id: string; event: KernelEvent; cause: Cause };

/** An event a replacement brings with it, raised through its own stage. */
export type FollowUp = { event: KernelEvent; stage: Stage<unknown> };

/**
 * A rewrite that brings follow-ups: `then` lands right after the rewritten event happens, before
 * its triggers, each caused by `{ event: <rewritten>, listener: <replacement> }`.
 */
export type Replaced = { event: KernelEvent; then?: readonly FollowUp[] };

/**
 * A replacement: rewrites the proposed event (alone, or with follow-ups), or cancels it by
 * returning `null`. Each applies at most once. A replacement that prompts throws, and the intent
 * re-runs from the top (`decisions/0212` item 6); one that draws, draws from the loop's dice.
 * `refund` marks one that, when it cancels a declaration, refunds the declaration's cost
 * (`decisions/0212` item 9: otherwise declaring spends).
 */
export type Replacement = { id: string; replace: (event: KernelEvent) => KernelEvent | Replaced | null; refund?: true };

/** How one raise runs: the replacements offered, what landing does, and the triggers after. */
export type Stage<T> = {
  replacements?: readonly Replacement[];
  /** Grounds the event. A fact only landing settles (a roll's faces) is written onto the event here, before the log records it. */
  happen?: (event: KernelEvent) => void;
  /** Told which replacement cancelled the event, when one did. */
  cancelled?: (by: Replacement) => void;
  /** Reacts to the landed event; its return is {@link EventLoop.raise}'s. */
  trigger: (happened: Happened) => T;
};

/** An action that opens after the current one finishes (`decisions/0212` item 3). */
export type QueuedAction = { kind: "forced_attack"; request: ForcedAttackRequest };

export type EventLoop = {
  /** The intent's one recording dice source. */
  readonly dice: DiceSource;
  /** Every event that happened, in landing order. */
  readonly log: readonly Happened[];
  /**
   * Propose, replace, happen, trigger. Returns `undefined` when a replacement cancelled the event.
   * `cause` defaults to the event whose triggers are running (a consequence), or `null` at the root.
   */
  raise<T>(event: KernelEvent, stage: Stage<T>, cause?: Cause): T | undefined;
  /**
   * Raise an event that stays running for the rest of the loop's life, so everything raised after it
   * defaults to it as cause: the intent's declaration. Returns `undefined` when a replacement
   * cancelled it.
   */
  open(event: KernelEvent, stage?: Omit<Stage<unknown>, "trigger">, cause?: Cause): Happened | undefined;
  /** The event whose triggers are running (or the open declaration), if any. */
  current(): Happened | undefined;
  /** Queue a new action behind every action already queued. */
  queueAction(action: QueuedAction): void;
  /** The next queued action, first in first out, or `undefined` when the queue is empty. */
  nextAction(): QueuedAction | undefined;
  /**
   * **The fold** (`decisions/0212` item 4). The scratch state for a stage that starts from `base`:
   * `base` with every step landed on it folded in. Each base keeps its own scratch: a new
   * resolution (or a followup over committed state) starts fresh on its own base, every stage of
   * one resolution shares the entry state's, and a stage nested over another base (a save a
   * listener forces) folds into its own without disturbing the outer one.
   */
  scratch(base: EncounterState): EncounterState;
  /**
   * Fold grounded steps into the scratch for `base` as they land. A step object lands once: a
   * step already landed (a listener's, later appended to its resolution's record) is skipped, so a
   * stage can pass its whole record and only the new steps fold.
   */
  land(base: EncounterState, steps: readonly ResolvedStep[]): void;
  /**
   * **The boundary** (`event-engine-cascades` C2): run `land` (one damage packet's landing, or one
   * heat gain outside a packet), then deliver every listener {@link defer}red inside it, in the order
   * deferred, each with its own event as the cause. A delivery that defers more queues behind the
   * rest. A boundary opened inside another joins it, so the outermost delivers. A throw (a halt)
   * drops the queue: the intent re-runs from the top.
   */
  boundary<T>(land: () => T): T;
  /** Queue `deliver` for `happened` at the open boundary, or run it now, caused by `happened`, when none is open. */
  defer(happened: Happened, deliver: () => void): void;
};

/** A fresh loop over `dice`, one per intent (or per fire, for a caller not yet threaded one). */
export function createEventLoop(args: { dice: DiceSource }): EventLoop {
  const log: Happened[] = [];
  const actions: QueuedAction[] = [];
  const running: Happened[] = [];
  let next = 0;
  // One scratch per stage base, all kept: a stage nested over another base (a save a listener
  // forces fires over that listener's scratch) folds into its own, and the outer one is intact
  // when control returns to it.
  const seats = new WeakMap<EncounterState, { state: EncounterState; landed: WeakSet<ResolvedStep> }>();
  const seated = (base: EncounterState) => {
    let at = seats.get(base);
    if (at === undefined) seats.set(base, (at = { state: base, landed: new WeakSet() }));
    return at;
  };
  /** Propose, replace and happen: the landed event, or `undefined` when a replacement cancelled it. */
  const land = (proposed: KernelEvent, stage: Omit<Stage<unknown>, "trigger">, cause: Cause | undefined): Happened | undefined => {
    let event: KernelEvent = proposed;
    const followUps: { by: string; then: readonly FollowUp[] }[] = [];
    for (const r of stage.replacements ?? []) {
      const out = r.replace(event);
      if (out === null) {
        stage.cancelled?.(r);
        return undefined;
      }
      if ("kind" in out) event = out;
      else {
        event = out.event;
        if (out.then !== undefined) followUps.push({ by: r.id, then: out.then });
      }
    }
    stage.happen?.(event);
    const parent = running[running.length - 1];
    const happened: Happened = { id: `e${next++}`, event, cause: cause !== undefined ? cause : parent !== undefined ? { event: parent.id } : null };
    log.push(happened);
    for (const { by, then } of followUps) for (const f of then) raise(f.event, f.stage, { event: happened.id, listener: by });
    return happened;
  };
  const raise = <T>(proposed: KernelEvent, stage: Stage<T>, cause?: Cause): T | undefined => {
    const happened = land(proposed, stage, cause);
    if (happened === undefined) return undefined;
    // Popped even when the trigger throws: a caller inside the same intent may catch the throw
    // (`resolveForcedAttacks` turns a nested `ResolveError` into a skipped attack) and go on
    // raising, and a stale parent would mis-cause every later event.
    running.push(happened);
    try {
      return stage.trigger(happened);
    } finally {
      running.pop();
    }
  };
  let deferred: { happened: Happened; deliver: () => void }[] | undefined;
  const deliver = (happened: Happened, run: () => void): void => {
    running.push(happened);
    try {
      run();
    } finally {
      running.pop();
    }
  };
  return {
    dice: args.dice,
    log,
    raise,
    open(proposed, stage = {}, cause) {
      const happened = land(proposed, stage, cause);
      if (happened !== undefined) running.push(happened);
      return happened;
    },
    current: () => running[running.length - 1],
    queueAction(action) {
      actions.push(action);
    },
    nextAction() {
      return actions.shift();
    },
    scratch: (base) => seated(base).state,
    land(base, steps) {
      const at = seated(base);
      const fresh = steps.filter((st) => !at.landed.has(st));
      if (fresh.length === 0) return;
      for (const st of fresh) at.landed.add(st);
      at.state = applyResolved(at.state, fresh);
    },
    boundary(land) {
      if (deferred !== undefined) return land();
      const queue: { happened: Happened; deliver: () => void }[] = (deferred = []);
      try {
        const out = land();
        for (let next = queue.shift(); next !== undefined; next = queue.shift()) deliver(next.happened, next.deliver);
        return out;
      } finally {
        deferred = undefined;
      }
    },
    defer(happened, run) {
      if (deferred !== undefined) deferred.push({ happened, deliver: run });
      else deliver(happened, run);
    },
  };
}

/**
 * The state a stage's listeners are gathered from (`decisions/0212` item 5): the loop's scratch for
 * `base` when the stage holds a loop, else `base`. A status a listener added earlier in the action
 * brings its own listeners to every later event.
 */
export function scratchOf(loop: EventLoop | undefined, base: EncounterState): EncounterState {
  return loop !== undefined ? loop.scratch(base) : base;
}
